'use client';
import { ProgressBar } from '@wwe-portal/ui/components/progress/progress-bar';
import { formatDateTime } from '@wwe-portal/utils/date';
import { useTranslations } from 'next-intl';
import React from 'react';
import { DateTime } from 'luxon';
import { ClientOnly } from '../../../../../components/common/client-only';

interface ProgressProps {
  endDate: string;
  startDate: string;
}

function Progress({ endDate, startDate }: Readonly<ProgressProps>) {
  const t = useTranslations();
  /**
   * Calculate duration between date now/startDate and event endDate
   * Format: X Days Y Hours Remaining
   */
  const date1 = DateTime.fromISO(endDate);
  const dateNow = DateTime.now();
  const duration = date1.diff(dateNow, ['days', 'hours', 'minutes']);
  /**
   * Calculate Progress bar percents
   */
  const sDate = startDate ? DateTime.fromISO(startDate) : null;
  const eDate = endDate ? DateTime.fromISO(endDate) : null;
  const fullProgress = sDate && eDate ? sDate.diff(eDate, 'days').days : 0;
  const currentProgress = sDate ? sDate.diff(dateNow, 'days').days : 0;
  const progressInPercents = (currentProgress * 100) / fullProgress;
  const inProcess = progressInPercents >= 0 && progressInPercents < 100;

  /**
   * Generate Progress bar Date and time
   * @param duration
   */
  const generateProgressDate = (duration: {
    days: number;
    hours: number;
    minutes: number;
  }) => {
    const { days, hours, minutes } = duration;

    if (days && hours) {
      return `${days} ${t('days')} ${hours} ${t('hours')} ${t('remaining')}`;
    } else if (hours && !days) {
      return `${hours} ${t('hours')} ${t('remaining')}`;
    } else if (!hours && days) {
      return `${days} ${t('days')} ${t('remaining')}`;
    }

    return `${minutes} ${t('minutes')} ${t('remaining')}`;
  };

  const getDisplayText = () => {
    const formattedEndDate = eDate
      ? formatDateTime(eDate.toISO() ?? '', 'MMMM DD, HH:mm')
      : '';
    const formattedStartDate = sDate
      ? formatDateTime(sDate.toISO() ?? '', 'MMMM DD, HH:mm')
      : '';

    if (inProcess) {
      return generateProgressDate(duration);
    }

    if (sDate !== null && sDate < dateNow) {
      return `${t('ended_on')} ${formattedEndDate}`;
    }

    return `${t('starts_on')} ${formattedStartDate}`;
  };

  return (
    <div className="relative px-5 pb-[15px]">
      <hr className="border-t-bayOfMany mb-[10px]" />
      <p className="text-text600 mb-[10px] pt-[3px] text-sm">
        {getDisplayText()}
      </p>

      <div className="flex items-center justify-between">
        <div className="w-full">
          <ClientOnly>
            <ProgressBar
              hideValue
              value={inProcess ? 100 - progressInPercents : 0}
              maxValue={100}
              barHeight={6}
              color="#00baff"
              className="w-full"
            />
          </ClientOnly>
        </div>
      </div>
    </div>
  );
}

export default Progress;
